import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faFlask, faLightbulb } from '@fortawesome/free-solid-svg-icons';

const CoreValuesSection = styled.section`
  padding: 100px 0;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    width: 30px;
    height: 1px;
    background-color: #ff6e6e;
  }
  
  &:before {
    left: -40px;
  }
  
  &:after {
    right: -40px;
  }
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ValuesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  background-color: #fff;
  border-radius: 8px;
  padding: 40px 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 0;
    background-color: #ff6e6e;
    transition: height 0.5s ease;
    z-index: -1;
  }
  
  &:hover:before {
    height: 100%;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const IconBox = styled.div`
  width: 70px;
  height: 70px;
  background-color: rgba(255, 94, 20, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  color: #ff6e6e;
  font-size: 28px;
  transition: all 0.3s ease;
  
  ${ValueCard}:hover & {
    background-color: #ff6e6e;
    color: #fff;
  }
`;

const ValueTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const ValueDescription = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 0;
`;

const ShapeElement = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 94, 20, 0.05);
  z-index: 0;
  
  &.shape1 {
    top: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
  }
  
  &.shape2 {
    bottom: -50px;
    left: -50px;
    width: 200px;
    height: 200px;
  }
`;

// Core values data
const coreValues = [
  {
    id: 1,
    icon: faHandshake,
    title: 'Long-term commitment',
    description: `${new Date().getFullYear() - 2018}+ years of running a profitable organization gives us a good sense of challenges that a growing business faces. We take pride in running a sustainable business that's powered by you, our customer.`
  },
  {
    id: 2,
    icon: faFlask,
    title: 'Focus on research and development',
    description: 'Software is our craft and we back it up with our relentless investments in R&D. So much so that we prefer to own the entire technology stack, including running our data centers globally.'
  },
  {
    id: 3,
    icon: faLightbulb,
    title: 'Innovation and Excellence',
    description: 'We are driven by innovation, integrity, and a commitment to excellence. By embracing change, working collaboratively, and staying customer-focused, we strive to create meaningful, lasting impact in everything we do.'
  }
];

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 * i,
      duration: 0.5,
    }
  })
};

const CoreValues = () => {
  return (
    <CoreValuesSection id="core-values">
      <ShapeElement className="shape1" />
      <ShapeElement className="shape2" />
      
      <Container>
        <SectionTitle>
          <SubTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Our Core Values
          </SubTitle>
          <Title
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            The core values and <span>principles</span> that drive us
          </Title>
          <Description
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            These fundamental beliefs guide our decisions and shape our culture as we strive to deliver exceptional solutions for our clients.
          </Description>
        </SectionTitle>
        
        <ValuesWrapper>
          {coreValues.map((value, index) => (
            <ValueCard
              key={value.id}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <IconBox>
                <FontAwesomeIcon icon={value.icon} />
              </IconBox>
              
              <ValueTitle>{value.title}</ValueTitle>
              <ValueDescription>{value.description}</ValueDescription>
            </ValueCard>
          ))}
        </ValuesWrapper>
      </Container>
    </CoreValuesSection>
  );
};

export default CoreValues;